import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    CircularProgress,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    TableSortLabel,
    Alert,
    Tooltip,
    IconButton,
    Container 
} from "@mui/material";
import { Delete, Edit, Save, Cancel, FilterList, Clear, PersonSearch as PersonSearchIcon } from "@mui/icons-material";
import config from './config.js';
import api from './api';
import { useNavigate } from 'react-router-dom';

// Define roles for clarity (used as a fallback if role_name is not present)
const ROLES_FALLBACK = {
    1: "Admin",
    2: "Moderator",
    3: "User",
};

// --- Sorting Helpers ---
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
  let valA = a[orderBy];
  let valB = b[orderBy];

  const aIsNull = valA === null || typeof valA === 'undefined';
  const bIsNull = valB === null || typeof valB === 'undefined';

  if (aIsNull && bIsNull) return 0;
  if (aIsNull) return 1;
  if (bIsNull) return -1;

  if (orderBy === 'user_id') {
    valA = Number(valA);
    valB = Number(valB);
  }

  if (typeof valA === 'number' && typeof valB === 'number') {
    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
  }
  valA = String(valA).toLowerCase();
  valB = String(valB).toLowerCase();
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

const headCells = [
  { id: 'user_id', numeric: true, disablePadding: false, label: 'User ID' },
  { id: 'name', numeric: false, disablePadding: false, label: 'Name' },
  { id: 'email', numeric: false, disablePadding: false, label: 'Email' },
  { id: 'team_name', numeric: false, disablePadding: false, label: 'Team' },
  { id: 'role_name', numeric: false, disablePadding: false, label: 'Role' },
  { id: 'is_verified', numeric: false, disablePadding: false, label: 'Verified' },
  { id: 'actions', numeric: false, disablePadding: false, label: 'Actions', sortable: false },
];


function UserManagement({ currentAdminUserId, currentAdminName }) {
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [rolesData, setRolesData] = useState([]); 
    const [loading, setLoading] = useState(true); 
    const [actionLoading, setActionLoading] = useState(false); 
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    
    const [editingUser, setEditingUser] = useState(null); 
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role_id: 3, team_id: '' }); 
    const [showAddUserForm, setShowAddUserForm] = useState(false);

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('user_id'); 

    const [filterName, setFilterName] = useState('');
    const [filterEmail, setFilterEmail] = useState('');
    const [filterTeam, setFilterTeam] = useState('');
    const [filterRole, setFilterRole] = useState(''); 
    const [showFilters, setShowFilters] = useState(true); 

    const [impersonationLoadingUserId, setImpersonationLoadingUserId] = useState(null); 
    const [impersonationError, setImpersonationError] = useState(null);

    const navigate = useNavigate();

    const fetchAllData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [usersResponse, teamsResponse, rolesResponse] = await Promise.all([
                api.get(`${config.backendUrl}/users`),
                api.get(`${config.backendUrl}/teams`),
                api.get(`${config.backendUrl}/roles`) 
            ]);
            setUsers(usersResponse.data || []);
            setTeams(teamsResponse.data || []);
            setRolesData(rolesResponse.data || []); 
        } catch (err) {
            console.error("Error fetching initial data:", err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch initial data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    useEffect(() => {
        let timer;
        if (successMessage) {
            timer = setTimeout(() => setSuccessMessage(null), 3000);
        } else if (error) {
            timer = setTimeout(() => setError(null), 5000);
        } else if (impersonationError) {
            timer = setTimeout(() => setImpersonationError(null), 5000);
        }
        return () => clearTimeout(timer);
    }, [successMessage, error, impersonationError]);

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleEdit = (user) => {
        setEditingUser({ ...user, team_id: user.team_id || '', role_id: user.role_id || 3 }); 
        setShowAddUserForm(false); 
    };

    const handleCancelEdit = () => {
        setEditingUser(null);
    };

    const handleEditingUserChange = (e) => {
        const { name, value } = e.target;
        setEditingUser(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveUser = async () => {
        if (!editingUser) return;
        setActionLoading(true); setError(null); setSuccessMessage(null);
        try {
            const payload = { ...editingUser };
            payload.team_id = payload.team_id === '' || payload.team_id === null ? null : parseInt(payload.team_id, 10);
            payload.role_id = parseInt(payload.role_id, 10);
            
            // The backend should ideally return the full updated user object,
            // including the new team_name and cohort_id/cohort_name if the team change affected the cohort.
            const response = await api.put(`${config.backendUrl}/users/${editingUser.user_id}`, payload);
            const updatedUserDataFromApi = response.data; // Assuming API returns the updated user

            setSuccessMessage('User updated successfully!');
            
            // If the admin edited their own details, update localStorage and trigger App.js state sync
            if (editingUser.user_id.toString() === currentAdminUserId.toString()) {
                console.log("Admin edited their own profile. Updating local session data.");
                localStorage.setItem('userTeamId', updatedUserDataFromApi.team_id ? updatedUserDataFromApi.team_id.toString() : '');
                // Assuming backend now returns cohort_id with the updated user
                localStorage.setItem('userCohortId', updatedUserDataFromApi.cohort_id ? updatedUserDataFromApi.cohort_id.toString() : '');
                
                // Dispatch event for App.js to sync auth state (which reads from localStorage)
                window.dispatchEvent(new Event('authChanged'));
            }

            setEditingUser(null); 
            fetchAllData(); // Refresh the user list in UserManagement
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleNewUserChange = (e) => {
        const { name, value } = e.target;
        setNewUser({ ...newUser, [name]: value });
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setActionLoading(true); setError(null); setSuccessMessage(null);
        try {
            const payload = { ...newUser };
            payload.team_id = payload.team_id === '' ? null : parseInt(payload.team_id, 10);
            payload.role_id = parseInt(payload.role_id, 10);
            
            await api.post(`${config.backendUrl}/users`, payload);
            setSuccessMessage('User created successfully!');
            setNewUser({ name: '', email: '', password: '', role_id: 3, team_id: '' }); 
            setShowAddUserForm(false);
            fetchAllData(); 
        } catch (err) { 
            setError(err.response?.data?.message || 'Failed to create user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (userIdToDelete) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            setActionLoading(true); setError(null); setSuccessMessage(null);
            try {
                await api.delete(`${config.backendUrl}/users/${userIdToDelete}`);
                setSuccessMessage('User deleted successfully!');
                fetchAllData(); 
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete user');
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleImpersonate = async (targetUserId, targetUserName) => {
        if (!currentAdminUserId || !currentAdminName) {
            setImpersonationError("Admin details not available. Cannot start impersonation.");
            console.error("Admin details (currentAdminUserId or currentAdminName) are missing from props.");
            return;
        }

        setImpersonationLoadingUserId(targetUserId);
        setImpersonationError(null);
        try {
            const originalAdminToken = localStorage.getItem('token');
            if (!originalAdminToken) {
                throw new Error("Original admin token not found in localStorage. Please log in again.");
            }

            localStorage.setItem('original_admin_token', originalAdminToken);
            localStorage.setItem('original_admin_userId', currentAdminUserId.toString());
            localStorage.setItem('original_admin_userName', currentAdminName);
            localStorage.setItem('isImpersonating', 'true'); 

            const response = await api.post(`${config.backendUrl}/api/admin/impersonate/${targetUserId}`);
            const { impersonation_token } = response.data;

            if (impersonation_token) {
                localStorage.setItem('token', impersonation_token);
                window.dispatchEvent(new Event('authChanged'));
                navigate('/dashboard'); 
            } else {
                throw new Error("Impersonation token not received from the server.");
            }

        } catch (err) {
            console.error("Impersonation failed:", err);
            setImpersonationError(err.response?.data?.message || err.message || 'Failed to start impersonation.');
            localStorage.removeItem('original_admin_token');
            localStorage.removeItem('original_admin_userId');
            localStorage.removeItem('original_admin_userName');
            localStorage.removeItem('isImpersonating'); 
        } finally {
            setImpersonationLoadingUserId(null);
        }
    };

    const displayedUsers = useMemo(() => {
        let filteredUsers = users.filter(user => 
            (filterName === '' || (user.name && user.name.toLowerCase().includes(filterName.toLowerCase()))) &&
            (filterEmail === '' || (user.email && user.email.toLowerCase().includes(filterEmail.toLowerCase()))) &&
            (filterTeam === '' || (user.team_id && user.team_id.toString() === filterTeam)) &&
            (filterRole === '' || (user.role_id && user.role_id.toString() === filterRole))
        );
        return stableSort(filteredUsers, getComparator(order, orderBy));
    }, [users, order, orderBy, filterName, filterEmail, filterTeam, filterRole]);


    if (loading) return <Container sx={{py:3, textAlign: 'center'}}><CircularProgress /></Container>;
    
    return (
        <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" gutterBottom component="h1" sx={{ textAlign: 'center', mb: 3 }}>User Management</Typography>

            {successMessage && <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>{successMessage}</Alert>}
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {impersonationError && <Alert severity="error" onClose={() => setImpersonationError(null)} sx={{ mb: 2 }}>{impersonationError}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button variant="contained" onClick={() => { setShowAddUserForm(!showAddUserForm); if (editingUser) setEditingUser(null); }} sx={{minWidth: '180px'}}>
                    {showAddUserForm && !editingUser ? 'Cancel Adding User' : 'Add New User'}
                </Button>
                 <Box>
                    <Button variant="outlined" onClick={() => setShowFilters(!showFilters)} startIcon={<FilterList />} sx={{ mr: 1 }}>
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    {showFilters && ( 
                        <Button variant="outlined" color="secondary" onClick={() => { setFilterName(''); setFilterEmail(''); setFilterTeam(''); setFilterRole(''); }} startIcon={<Clear />}>
                            Clear Filters
                        </Button>
                    )}
                </Box>
            </Box>

            {showAddUserForm && !editingUser && (
                <Paper sx={{ p: 2, mb: 3, mt: 1 }} id="add-user-form">
                    <Typography variant="h6" gutterBottom>Add New User</Typography>
                    <Box component="form" onSubmit={handleAddUser} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField label="Name" name="name" value={newUser.name} onChange={handleNewUserChange} required size="small"/>
                        <TextField label="Email" name="email" type="email" value={newUser.email} onChange={handleNewUserChange} required size="small"/>
                        <TextField label="Password" name="password" type="password" value={newUser.password} onChange={handleNewUserChange} required size="small" helperText="Min 8 characters"/>
                        <FormControl fullWidth size="small" required>
                            <InputLabel id="new-user-role-label">Role</InputLabel>
                            <Select labelId="new-user-role-label" name="role_id" value={newUser.role_id} label="Role" onChange={handleNewUserChange}>
                                {rolesData.map(role => <MenuItem key={role.role_id} value={role.role_id}>{role.role_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="new-user-team-label">Team</InputLabel>
                            <Select labelId="new-user-team-label" name="team_id" value={newUser.team_id} label="Team" onChange={handleNewUserChange} displayEmpty>
                                <MenuItem value=""><em>None</em></MenuItem>
                                {teams.map(team => <MenuItem key={team.team_id} value={team.team_id}>{team.team_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button type="submit" variant="contained" disabled={actionLoading}>
                            {actionLoading ? <CircularProgress size={24} /> : 'Add User'}
                        </Button>
                    </Box>
                </Paper>
            )}
            
            {showFilters && (
                <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField label="Filter by Name" value={filterName} onChange={(e) => setFilterName(e.target.value)} size="small" sx={{flexGrow: 1, minWidth: '180px'}}/>
                    <TextField label="Filter by Email" value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} size="small" sx={{flexGrow: 1, minWidth: '180px'}}/>
                    <FormControl variant="outlined" size="small" sx={{flexGrow: 1, minWidth: '180px'}}> 
                        <InputLabel id="filter-team-label" shrink={true}>Filter by Team</InputLabel> 
                        <Select 
                            labelId="filter-team-label" 
                            value={filterTeam} 
                            label="Filter by Team" 
                            onChange={(e) => setFilterTeam(e.target.value)} 
                            displayEmpty
                        >
                            <MenuItem value="">All Teams</MenuItem>
                            {teams.map(team => <MenuItem key={team.team_id} value={team.team_id.toString()}>{team.team_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{flexGrow: 1, minWidth: '180px'}}> 
                        <InputLabel id="filter-role-label" shrink={true}>Filter by Role</InputLabel> 
                        <Select 
                            labelId="filter-role-label" 
                            value={filterRole} 
                            label="Filter by Role" 
                            onChange={(e) => setFilterRole(e.target.value)} 
                            displayEmpty
                        >
                            <MenuItem value="">All Roles</MenuItem>
                            {rolesData.map(role => <MenuItem key={role.role_id} value={role.role_id.toString()}>{role.role_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Paper>
            )}

            <TableContainer component={Paper}>
                <Table size="small" aria-label="user management table">
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell key={headCell.id} 
                                           align={headCell.numeric ? 'right' : 'left'}
                                           padding={headCell.disablePadding ? 'none' : 'normal'}
                                           sortDirection={orderBy === headCell.id ? order : false}>
                                    {headCell.sortable !== false ? (
                                        <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : 'asc'} onClick={() => handleSortRequest(headCell.id)}>
                                            {headCell.label}
                                        </TableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedUsers.map((user) => (
                            editingUser && editingUser.user_id === user.user_id ? (
                                // Inline Edit Row
                                <TableRow key={user.user_id} sx={{ '& > *': { py: 1 } }}> 
                                    <TableCell align="right">{user.user_id}</TableCell>
                                    <TableCell>
                                        <TextField name="name" value={editingUser.name} onChange={handleEditingUserChange} size="small" fullWidth variant="outlined"/>
                                    </TableCell>
                                    <TableCell>
                                        <TextField name="email" value={editingUser.email} onChange={handleEditingUserChange} size="small" fullWidth variant="outlined"/>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select name="team_id" value={editingUser.team_id || ''} onChange={handleEditingUserChange} displayEmpty variant="outlined">
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {teams.map(team => <MenuItem key={team.team_id} value={team.team_id}>{team.team_name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                         <FormControl fullWidth size="small">
                                            <Select name="role_id" value={editingUser.role_id} onChange={handleEditingUserChange} variant="outlined">
                                                {rolesData.map(role => <MenuItem key={role.role_id} value={role.role_id}>{role.role_name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>{user.is_verified ? 'Yes' : 'No'}</TableCell> 
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button onClick={handleSaveUser} size="small" variant="contained" startIcon={<Save/>} disabled={actionLoading}>
                                                {actionLoading ? <CircularProgress size={20}/> : 'Save'}
                                            </Button>
                                            <Button onClick={handleCancelEdit} size="small" variant="outlined" startIcon={<Cancel/>}>Cancel</Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                // Display Row
                                <TableRow key={user.user_id} hover>
                                    <TableCell align="right">{user.user_id}</TableCell>
                                    <TableCell>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.team_name || 'N/A'}</TableCell>
                                    <TableCell>{user.role_name || ROLES_FALLBACK[user.role_id] || 'N/A'}</TableCell>
                                    <TableCell>{user.is_verified ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start', flexWrap: 'nowrap' }}>
                                            <Tooltip title="Edit User">
                                                <IconButton size="small" onClick={() => handleEdit(user)} color="primary" disabled={actionLoading || !!editingUser}><Edit /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete User">
                                                <IconButton size="small" onClick={() => handleDelete(user.user_id)} color="error" disabled={actionLoading || !!editingUser || (currentAdminUserId === user.user_id)}>
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                            {currentAdminUserId && currentAdminUserId !== user.user_id && (
                                                <Tooltip title={`View as ${user.name}`}>
                                                    <span> 
                                                    <IconButton 
                                                        size="small" 
                                                        color="secondary"
                                                        onClick={() => handleImpersonate(user.user_id, user.name)}
                                                        disabled={impersonationLoadingUserId === user.user_id || actionLoading || !!editingUser}
                                                    >
                                                        {impersonationLoadingUserId === user.user_id ? <CircularProgress size={20} color="inherit"/> : <PersonSearchIcon />}
                                                    </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )
                        ))}
                        {displayedUsers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length} align="center" sx={{p:2}}>
                                    No users match the current filters or no users available.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
}

export default UserManagement;
